/**
 * pages/login.tsx
 *
 * Sign-in screen — nickname + password.
 * On success, stores JWT and navigates to /home.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLogin } from '../hooks/use-auth'
import { isAuthenticated } from '../store/auth'

export default function LoginPage() {
  const navigate = useNavigate()
  const { mutate: login, isPending, error } = useLogin()

  useEffect(() => {
    if (isAuthenticated()) navigate('/home', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [nickname, setNickname] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [nicknameError, setNicknameError] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const validate = () => {
    let ok = true
    if (!nickname.trim()) {
      setNicknameError('Please enter your nickname')
      ok = false
    } else {
      setNicknameError('')
    }
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      ok = false
    } else {
      setPasswordError('')
    }
    return ok
  }

  const handleSubmit = () => {
    if (!validate()) return
    login(
      { nickname: nickname.trim(), password },
      { onSuccess: () => navigate('/home', { replace: true }) },
    )
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] px-5 pt-[env(safe-area-inset-top)] pb-2">
        <div className="flex items-center gap-2 h-12">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-[#D4537E] font-['DM_Sans'] text-[15px] active:opacity-60 transition-opacity duration-100 touch-manipulation -ml-1"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-5 pb-[88px] overflow-y-auto">
        <div className="animate-fade-in-up">
          {/* Title */}
          <div className="mb-8 [animation-delay:60ms] animate-fade-in-up">
            <h1 className="font-['Playfair_Display'] text-[28px] font-semibold text-[#2C1810] leading-tight mb-2">
              Welcome back 💕
            </h1>
            <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62]">
              Sign in to continue your story.
            </p>
          </div>

          {/* Form */}
          <div className="flex flex-col gap-3 [animation-delay:90ms] animate-fade-in-up">
            {/* Nickname */}
            <div>
              <label className="block font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-1.5">
                Nickname
              </label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="Your nickname"
                maxLength={50}
                autoComplete="username"
                autoFocus
                className={`
                  w-full h-[52px] px-4
                  bg-[#FAF8F4] text-[#2C1810]
                  font-['DM_Sans'] text-[15px]
                  rounded-[12px]
                  border-[1.5px] outline-none
                  placeholder:text-[#B89AA2]
                  transition-all duration-150
                  focus:border-[#D4537E] focus:shadow-[0_0_0_3px_rgba(212,83,126,0.12)]
                  ${nicknameError ? 'border-[#C0392B]' : 'border-[#F4C0D1]'}
                `}
              />
              {nicknameError && (
                <p className="mt-1 font-['DM_Sans'] text-[12px] text-[#C0392B]">{nicknameError}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  placeholder="Your password"
                  maxLength={100}
                  autoComplete="current-password"
                  className={`
                    w-full h-[52px] px-4 pr-12
                    bg-[#FAF8F4] text-[#2C1810]
                    font-['DM_Sans'] text-[15px]
                    rounded-[12px]
                    border-[1.5px] outline-none
                    placeholder:text-[#B89AA2]
                    transition-all duration-150
                    focus:border-[#D4537E] focus:shadow-[0_0_0_3px_rgba(212,83,126,0.12)]
                    ${passwordError ? 'border-[#C0392B]' : 'border-[#F4C0D1]'}
                  `}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B89AA2] text-[18px] touch-manipulation select-none"
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              {passwordError && (
                <p className="mt-1 font-['DM_Sans'] text-[12px] text-[#C0392B]">{passwordError}</p>
              )}
            </div>

            {/* API error */}
            {error && (
              <p className="font-['DM_Sans'] text-[13px] text-[#C0392B] text-center">
                {error.message}
              </p>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className="
                w-full h-[52px] px-6 mt-2
                bg-[#D4537E] text-white
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px]
                shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                transition-all duration-150 ease-out
                active:bg-[#993556] active:scale-[0.98] active:shadow-none
                disabled:opacity-40 disabled:cursor-not-allowed
                select-none touch-manipulation
              "
            >
              {isPending ? '💕 Signing in…' : 'Sign in'}
            </button>

            {/* Switch to register */}
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center mt-1">
              New here?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-[#D4537E] font-medium active:opacity-60 touch-manipulation"
              >
                Create an account →
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
